/**
 * Shared constants for case expense components.
 * Single source of truth for expense type, status, and payment method configuration.
 */

import {
  Mail as MailIcon,
  MoreHorizontal,
  Shield,
  Stethoscope,
} from 'lucide-react';

import type {
  CaseExpenseStatus,
  CaseExpenseType,
  PaymentMethod,
} from '@/services/api/generated/caseExpensesService';

/**
 * Expense type configuration for form controls and table badges.
 */
export const EXPENSE_TYPE_CONFIG: Record<
  CaseExpenseType,
  {
    label: string;
    icon: typeof Shield;
    badgeClass: string;
  }
> = {
  POLICE: {
    label: 'Police',
    icon: Shield,
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  MEDICAL: {
    label: 'Medical',
    icon: Stethoscope,
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  MAIL: {
    label: 'Mail',
    icon: MailIcon,
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  OTHER: {
    label: 'Other',
    icon: MoreHorizontal,
    badgeClass: 'bg-gray-100 text-gray-800 border-gray-200',
  },
};

/**
 * Status configuration for form controls and table badges.
 */
export const STATUS_CONFIG: Record<
  CaseExpenseStatus,
  {
    label: string;
    formClass: string;
    badgeClass: string;
  }
> = {
  UNPAID: {
    label: 'Unpaid',
    formClass: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
    badgeClass: 'bg-red-100 text-red-800 border-red-200',
  },
  SENT: {
    label: 'Sent',
    formClass: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  PAID: {
    label: 'Paid',
    formClass: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
    badgeClass: 'bg-green-100 text-green-800 border-green-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    formClass: 'bg-zinc-200 text-zinc-600 ring-1 ring-zinc-300',
    badgeClass: 'bg-gray-100 text-gray-500 border-gray-200',
  },
};

/**
 * Payment method display labels.
 */
export const PAYMENT_METHOD_CONFIG: Record<PaymentMethod, string> = {
  CREDIT_CARD: 'Credit Card',
  CHEQUE: 'Cheque',
  FIRM_PAYS: 'Firm Pays',
};

/**
 * Contact field labels (Apple Contacts style).
 */
export const CONTACT_LABELS = {
  phone: ['main', 'mobile', 'home', 'work', 'fax'] as const,
  email: ['work', 'home', 'other'] as const,
  address: ['main', 'work', 'home', 'billing', 'shipping'] as const,
};
